import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Download, Upload, Loader2, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

/**
 * Founder UAT post-168 (B) — safe competency batch import.
 * Flow: download template → upload → server-side preview/validation →
 * explicit confirmation → import (valid rows only; errors excluded & reported).
 */
export const CompetencyImportDialog = ({ open, onOpenChange, onImported }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setPreview(null); setResult(null); setFileName(''); if (fileRef.current) fileRef.current.value = ''; };

  const downloadTemplate = async () => {
    try {
      const res = await adminApi.competencyImportTemplate();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'competencies_import_template_v2.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setBusy(true);
    try {
      const data = await adminApi.competencyImportPreview(file);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      toast.error(t('pages.competencies.import.parseFailed', { defaultValue: 'Could not read the file' }), { description: formatApiError(err) });
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const res = await adminApi.competencyImportCommit(preview.rows);
      setResult(res);
      setPreview(null);
      toast.success(t('pages.competencies.import.doneToast', {
        defaultValue: '{{n}} competencies imported', n: res.imported,
      }));
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const issueLabel = (i) => t(`pages.competencies.import.issues.${i.code}`, {
    defaultValue: i.code.replace(/_/g, ' '), value: i.value || '',
  });

  const summary = preview?.summary;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl" data-testid="comp-import-dialog">
        <DialogHeader>
          <DialogTitle>{t('pages.competencies.import.title', { defaultValue: 'Import competencies' })}</DialogTitle>
          <DialogDescription>
            {t('pages.competencies.import.desc', {
              defaultValue: 'Upload an .xlsx or .csv file. Nothing is written until you review the preview and confirm. Existing competencies are never overwritten.',
            })}
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="comp-import-template-btn" className="gap-1.5">
              <Download className="h-4 w-4" /> {t('pages.competencies.import.template', { defaultValue: 'Download template' })}
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} data-testid="comp-import-file-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="comp-import-upload-btn" className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {t('pages.competencies.import.upload', { defaultValue: 'Upload file' })}
            </Button>
            {fileName && <span className="text-xs text-muted-foreground truncate max-w-[220px]">{fileName}</span>}
          </div>
        )}

        {summary && (
          <>
            <div className="flex items-center gap-2 flex-wrap" data-testid="comp-import-summary">
              <Badge className="bg-success/10 text-success border-success/30">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {t('pages.competencies.import.ready', { defaultValue: 'Ready to import: {{n}}', n: summary.ready + summary.warnings })}
              </Badge>
              <Badge className="bg-warning/10 text-warning border-warning/30">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {t('pages.competencies.import.warnings', { defaultValue: 'Warnings: {{n}}', n: summary.warnings })}
              </Badge>
              <Badge className="bg-destructive/10 text-destructive border-destructive/30">
                <XCircle className="h-3 w-3 mr-1" />
                {t('pages.competencies.import.errors', { defaultValue: 'Errors (excluded): {{n}}', n: summary.errors })}
              </Badge>
            </div>
            <div className="border rounded-md max-h-72 overflow-y-auto" data-testid="comp-import-preview">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/70">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-1.5 px-2">#</th>
                    <th className="py-1.5 px-2">{t('pages.competencies.name', { defaultValue: 'Name' })}</th>
                    <th className="py-1.5 px-2">{t('pages.competencies.code', { defaultValue: 'Code' })}</th>
                    <th className="py-1.5 px-2">{t('pages.competencies.category', { defaultValue: 'Category' })}</th>
                    <th className="py-1.5 px-2">{t('pages.competencies.import.status', { defaultValue: 'Status' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row_number} className={`border-t ${r.status === 'error' ? 'bg-destructive/5' : ''}`} data-testid={`comp-import-row-${r.row_number}`}>
                      <td className="py-1.5 px-2 tabular-nums text-muted-foreground">{r.row_number}</td>
                      <td className="py-1.5 px-2 font-medium">{r.name || '—'}</td>
                      <td className="py-1.5 px-2 font-mono">{r.code || '—'}{r.code_derived && <span className="text-muted-foreground"> *</span>}</td>
                      <td className="py-1.5 px-2">{r.category || '—'}</td>
                      <td className="py-1.5 px-2">
                        {r.status === 'ok' && <Badge variant="outline" className="text-xs text-success border-success/40">OK</Badge>}
                        {r.status !== 'ok' && (
                          <span className={`text-xs ${r.status === 'error' ? 'text-destructive' : 'text-warning'}`}>
                            {r.issues.map(issueLabel).join(' · ')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('pages.competencies.import.strategy', {
                defaultValue: '* code derived from name. On confirm, only rows without errors are imported; error rows are excluded and reported.',
              })}
            </p>
          </>
        )}

        {result && (
          <div className="space-y-2" data-testid="comp-import-result">
            <p className="text-sm font-semibold">
              {t('pages.competencies.import.resultTitle', { defaultValue: 'Import finished' })}
            </p>
            <p className="text-sm">
              <CheckCircle2 className="h-4 w-4 inline text-success mr-1" />
              {t('pages.competencies.import.resultImported', { defaultValue: '{{n}} imported', n: result.imported })}
              {result.skipped > 0 && (
                <span className="text-muted-foreground"> · {t('pages.competencies.import.resultSkipped', { defaultValue: '{{n}} skipped', n: result.skipped })}</span>
              )}
            </p>
            {result.skipped_rows?.length > 0 && (
              <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto border rounded-md p-2">
                {result.skipped_rows.map((r) => (
                  <p key={r.row_number}>#{r.row_number} {r.name || '—'} — {(r.issues || []).map(issueLabel).join(' · ')}</p>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="comp-import-cancel-btn">
            {result ? t('common.close', { defaultValue: 'Close' }) : t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          {summary && !result && (
            <Button onClick={confirm} disabled={busy || (summary.ready + summary.warnings) === 0} data-testid="comp-import-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              {t('pages.competencies.import.confirm', { defaultValue: 'Confirm import ({{n}})', n: summary.ready + summary.warnings })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompetencyImportDialog;
